'use strict';
// The server owns prices, discounts, tax, payment and orders. Never accept a
// client total as payment authority. No secrets or supplier URLs in this file.
(function (root) {
  function create({rootUrl, catalog, offers, fetcher = root.fetch.bind(root)}) {
    const base = rootUrl.endsWith('/') ? rootUrl : rootUrl + '/';
    const bySku = Object.fromEntries(Object.values(catalog).map(p => ['LM-' + p.reference.toUpperCase(), p]));
    async function request(path, body) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetcher(base + path, {
          method: body === undefined ? 'GET' : 'POST', credentials: 'same-origin', cache: 'no-store',
          headers: {'Accept': 'application/json', ...(body === undefined ? {} : {'Content-Type': 'application/json'})},
          ...(body === undefined ? {} : {body: JSON.stringify(body)}), signal: controller.signal
        });
        const data = await response.json();
        if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : 'Could not update your cart. Please try again.');
        return data;
      } catch (error) {
        if (error.name === 'AbortError') throw new Error('The connection timed out. Check your cart before trying again.');
        throw error;
      } finally { clearTimeout(timeout); }
    }
    async function variant(ref) {
      const expected = catalog[ref];
      if (!expected) throw new Error('That design is not in this collection.');
      const product = await request('products/' + encodeURIComponent(expected.handle) + '.js');
      const exact = product.variants?.find(v => v.sku === 'LM-' + ref.toUpperCase());
      if (!exact || product.handle !== expected.handle || !exact.available) throw new Error(expected.name + ' is currently unavailable. Please choose another design.');
      if (exact.price !== expected.unitCents) throw new Error('The price of ' + expected.name + ' has changed. Please contact us before ordering.');
      return exact;
    }
    const get = () => request('cart.js');
    function validate(cart) {
      const refs = [];
      for (const item of cart.items || []) {
        const p = bySku[item.sku];
        if (!p || item.original_price !== p.unitCents || !Number.isInteger(item.quantity) || item.quantity < 1) return {ok:false, reason:'Your cart includes an item outside this mask offer. Remove it before checking out.'};
        for (let i = 0; i < item.quantity; i++) refs.push(p.reference);
      }
      if (!refs.length) return {ok:false, refs, reason:'Your cart is empty.'};
      if (refs.length > 3) return {ok:false, refs, reason:'Choose one, two or three masks per crew. Reduce the quantity to continue.'};
      const expected = offers.pricesCents[refs.length];
      if (cart.currency !== 'USD') return {ok:false, refs, reason:'This offer is priced in USD. Please contact us if a different currency appears.'};
      if (cart.total_price !== expected) return {ok:false, refs, reason:'The bundle discount did not match the advertised total. No payment has been taken. Please contact us or try again shortly.'};
      return {ok:true, refs, merchandiseCents: cart.total_price, shippingCents: offers.shippingCents[refs.length]};
    }
    async function setCrew(refs) {
      if (!Array.isArray(refs) || refs.length < 1 || refs.length > 3 || refs.some(ref => !catalog[ref])) throw new Error('Choose one, two or three masks.');
      const unique = [...new Set(refs)];
      const variants = await Promise.all(unique.map(variant));
      const desired = Object.fromEntries(unique.map((ref, i) => [String(variants[i].id), refs.filter(value => value === ref).length]));
      const current = await get(), updates = {}, assigned = new Set();
      // One atomic set-quantity operation: network retries cannot double quantities.
      for (const item of current.items) {
        const id = String(item.variant_id);
        updates[item.key] = assigned.has(id) ? 0 : (desired[id] || 0);
        assigned.add(id);
      }
      for (const [id, quantity] of Object.entries(desired)) if (!assigned.has(id)) updates[id] = quantity;
      await request('cart/update.js', {updates, attributes: {'Crew selection': refs.map(ref => catalog[ref].name).join(' / ')}});
      return get();
    }
    async function change(key, quantity) {
      if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity > 3) throw new Error('Choose a quantity from zero to three.');
      await request('cart/change.js', {id:key, quantity});
      return get();
    }
    return {get, variant, validate, setCrew, change, bySku};
  }
  root.LMShopifyCart = {create};
  if (typeof module !== 'undefined' && module.exports) module.exports = {create};
})(typeof globalThis !== 'undefined' ? globalThis : this);
